import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/context/AuthContext";
import { SiteSettingsProvider } from "@/context/SiteSettingsContext";
import { PagesProvider } from "@/context/PagesContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";

import Home from "@/pages/Home";
import Destinations from "@/pages/Destinations";
import DestinationDetail from "@/pages/DestinationDetail";
import Journeys from "@/pages/Journeys";
import JourneyDetail from "@/pages/JourneyDetail";
import About from "@/pages/About";
import Journal from "@/pages/Journal";
import JournalArticle from "@/pages/JournalArticle";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Register from "@/pages/Register";

import PortalLayout from "@/pages/portal/PortalLayout";
import PortalHome from "@/pages/portal/PortalHome";
import PortalItinerary from "@/pages/portal/PortalItinerary";
import PortalDocuments from "@/pages/portal/PortalDocuments";
import PortalMessages from "@/pages/portal/PortalMessages";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import SiteSettings from "@/pages/admin/SiteSettings";
import PagesList from "@/pages/admin/PagesList";
import PageEdit from "@/pages/admin/PageEdit";
import TestimonialsList from "@/pages/admin/TestimonialsList";
import TeamList from "@/pages/admin/TeamList";
import MediaLibrary from "@/pages/admin/MediaLibrary";
import AccountSettings from "@/pages/admin/AccountSettings";
import DestinationsList from "@/pages/admin/DestinationsList";
import DestinationEdit from "@/pages/admin/DestinationEdit";
import JourneysList from "@/pages/admin/JourneysList";
import JourneyEdit from "@/pages/admin/JourneyEdit";
import JournalList from "@/pages/admin/JournalList";
import JournalEdit from "@/pages/admin/JournalEdit";
import EnquiriesList from "@/pages/admin/EnquiriesList";
import ExportData from "@/pages/admin/ExportData";
import ActivityLog from "@/pages/admin/ActivityLog";

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/destinations" element={<Destinations />} />
      <Route path="/destinations/:slug" element={<DestinationDetail />} />
      <Route path="/journeys" element={<Journeys />} />
      <Route path="/journeys/:slug" element={<JourneyDetail />} />
      <Route path="/about" element={<About />} />
      <Route path="/journal" element={<Journal />} />
      <Route path="/journal/:slug" element={<JournalArticle />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      <Route
        path="/portal"
        element={
          <ProtectedRoute>
            <PortalLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<PortalHome />} />
        <Route path="itinerary" element={<PortalItinerary />} />
        <Route path="documents" element={<PortalDocuments />} />
        <Route path="messages" element={<PortalMessages />} />
      </Route>

      {/* Admin */}
      <Route path="/studio/login" element={<AdminLogin />} />
      <Route
        path="/studio"
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="site-settings" element={<SiteSettings />} />
        <Route path="pages" element={<PagesList />} />
        <Route path="pages/:slug" element={<PageEdit />} />
        <Route path="testimonials" element={<TestimonialsList />} />
        <Route path="team" element={<TeamList />} />
        <Route path="destinations" element={<DestinationsList />} />
        <Route path="destinations/:slug" element={<DestinationEdit />} />
        <Route path="journeys" element={<JourneysList />} />
        <Route path="journeys/:slug" element={<JourneyEdit />} />
        <Route path="journal" element={<JournalList />} />
        <Route path="journal/:slug" element={<JournalEdit />} />
        <Route path="enquiries" element={<EnquiriesList />} />
        <Route path="activity" element={<ActivityLog />} />
        <Route path="export" element={<ExportData />} />
        <Route path="media" element={<MediaLibrary />} />
        <Route path="account" element={<AccountSettings />} />
      </Route>

      {/* Legacy admin path — silently redirects to /studio/login. Never exposes the dashboard. */}
      <Route path="/admin" element={<Navigate to="/studio/login" replace />} />
      <Route path="/admin/*" element={<Navigate to="/studio/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <SiteSettingsProvider>
          <PagesProvider>
            <BrowserRouter>
              <AppRouter />
              <Toaster position="top-center" richColors closeButton theme="light" />
            </BrowserRouter>
          </PagesProvider>
        </SiteSettingsProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
